import {
	Avatar,
	avatar,
	btn,
	cards,
	comp,
	Component,
	Config,
	DateTime,
	fieldset,
	h3, Notifier,
	p,
	router,
	tbar
} from "@intermesh/goui";
import {CurrentSection} from "./services/CurrentSection.js";
import {ServerApi} from "./util/ServerApi.js";
import {CurrentCourse} from "./services/CurrentCourse.js";
import {CurrentParticipant} from "./services/CurrentParticipant.js";
import {t} from "./util/Translate.js";
import {CourseEntity} from "./data/CoursesStore.js";
import {IfwFormat} from "./util/IfwFormat.js";
import {Attempt} from "./data/ParticipantsStore.js";

export class CourseSelect extends Component {

	private selections: Component;

	constructor() {
		super("section");
		this.itemId = "course-select";
		this.id = "course-select";
		this.cls = "ifw-card";
		this.title = t("Select your activity");

		this.items.add(
			comp({},
				cards({
						activeItem: 0
					}, fieldset(
						{},
						comp(
							{
								tagName: "div",
								cls: "ifw-title-cnt"
							}, h3({
								cls: "ifw-title",
								text: this.title
							})),
						this.selections = comp({
							cls: "hbox",
							style: {
								flexWrap: "wrap",
								justifyContent: "space-evenly"
							}
						}),
					),
				)
			),
			tbar({
					cls: "ifw-bbar"
				},
				btn({
					text: t("Back"),
					route: "profile"
				}),
				'->'
			)
		);
		const url = ServerApi.url("elearning/frontend/courses", {
			returnProperties: 'id,name,estimatedDuration,className,image,languages[*,language],sections,blocks[block]',
			q: [
				['andWhere', {'activities.activityId': null}],
				['joinRelation', 'activities', false, 'LEFT']
			],
			limit: 0
		});
		ServerApi.call(url, {}, "GET").then(async (result) => {
			const cs = await CurrentSection.get();
			let availableCourses = result.data.filter((item: CourseEntity) => {
				let hasLanguage = false;
				for (let i = 0, l = item.languages.length; i < l; i++) {
					if (item.languages[i].language.key.toUpperCase() === sessionStorage.getItem("language")) {
						hasLanguage = true;
						break;
					}
				}

				if (!hasLanguage) {
					return false;
				}

				for (let i = 0, l = item.sections.length; i < l; i++) {
					if (item.sections[i].sectionId == cs.id) {
						return true;
					}
				}

				return false;
			});

			availableCourses = availableCourses.sort((a: CourseEntity, b: CourseEntity) => {
				const nameA = a.name.toUpperCase(); // ignore upper and lowercase
				const nameB = b.name.toUpperCase(); // ignore upper and lowercase
				if (nameA < nameB) {
					return -1;
				}
				if (nameA > nameB) {
					return 1;
				}

				// names must be equal
				return 0;
			});

			for (const res of availableCourses) {
				let avaConfig = {
					title: res.name,
					height: 100,
					width: 100,
					cls: "goui-avatar centered",
				} as  Config<Avatar>;
				if (res.image) {
					avaConfig.backgroundImage = ServerApi.thumbUrl(res.image, 100, 100, true);
				} else {
					avaConfig.displayName = res.name;
				}

				this.selections.items.add(
					comp({
							cls: "vbox activities",
							style: {
								cursor: "pointer"
							},
							listeners: {
								added: (cmp) => {
									cmp.el.addEventListener("click", async (_elm) => {
										const p = {
											returnProperties: CurrentCourse.returnProperties,
											sectionId: null
										};
										const cs = CurrentSection.get();
										if(cs.id) {
											p.sectionId = cs.id;
										}
										const route = ServerApi.url(CurrentCourse.baseRoute+"/"+res.id, p);

										const result = await ServerApi.call(route);
										if (result.data) {
											sessionStorage.removeItem(CurrentParticipant.storageItem);
											await CurrentParticipant.readIf();
											sessionStorage.setItem(CurrentCourse.storageItem, JSON.stringify(result.data));
											// if(this.hasFinished(result.data)) {
												void router.goto("course-status");
											// } else {
											// 	void router.goto("course/" + res.id + "/");
											// }
										} else {
											Notifier.warning(t("Unable to retrieve course"));
										}
									});
								}
							}
						},
						comp({}, avatar(avaConfig)),
						comp({},
							p({
									text: res.name,
									cls: "lc-course-selection"
								},
							),
						),
						comp({
							flex: 1
						}),
						comp({
								tagName: "span",
								cls: "duration"
							},
							comp({
								tagName: "i",
								cls: "icon small",
								html: "timer",
							}),
							comp({
								tagName: "span",
								html: this.getTotalDuration(res)
							}),
							comp({
								tagName: "span",
								html: "&nbsp;(" + t("completed") + ")",
								hidden: !this.hasFinished(res)
							})
						)
					)
				)
			}
			this.unmask();
		});
		this.mask();

	}

	private getTotalDuration(item: CourseEntity): string {
		if(!item.estimatedDuration) {
			return t("Unknown");
		}

		const pc = CurrentParticipant.get(), now = new DateTime();
		let m = IfwFormat.ifwDurationToMinutes(item.estimatedDuration);
		for (const b of item.blocks) {
			const blockId = b.blockId;
			let hasCompleted = false;
			for (const a of pc.attempts!) {
				if (a.blockId != blockId) {
					continue;
				}
				hasCompleted = (a.succeeded && now.compare(new DateTime(a.expiresAt)) <= 0);
				if(hasCompleted) {
					break;
				}
			}
			if(hasCompleted && b.block.estimatedDuration) {
				m -= IfwFormat.ifwDurationToMinutes(b.block.estimatedDuration);
			}
		}
		return IfwFormat.ifwDuration("PT"+m+"M");

	}

	private hasFinished(item: CourseEntity, lastAttemptIsLeading?: boolean): boolean {
		lastAttemptIsLeading = lastAttemptIsLeading || false;

		const pc = CurrentParticipant.get(), now = new DateTime();
		let hasCompleted = true;
		for (const b of item.blocks) {
			let blockCompleted = false;
			const attempts = pc.attempts?.filter((att: Attempt) => {
				return att.blockId == b.blockId;
			} )
			for (const a of attempts!) {
				if(!a.succeeded) {
					continue;
				}
				blockCompleted = (now.compare(new DateTime(a.expiresAt)) <= 0);
				if (blockCompleted && !lastAttemptIsLeading) {
					break;
				}
			}
			hasCompleted = hasCompleted && blockCompleted;
		}
		return hasCompleted;
	}
}